/**
 * App-wide settings persisted to localStorage.
 *
 * Spec §4.4 L226: API keys are stored locally only after the user consents.
 * The store treats `keysConsented` as the gate — keys live in memory always,
 * but the persist middleware only writes the apiKeys map when the user has
 * explicitly opted in. Clearing consent wipes the persisted copy on next
 * commit but leaves the in-memory copy until reset() runs.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type LlmProvider = 'openai' | 'anthropic' | 'google';
export type AssistantMode = 'off' | 'suggest' | 'feedback' | 'both';

export type SettingsState = {
  llmProvider: LlmProvider;
  apiKeys: Readonly<Record<LlmProvider, string>>;
  assistantMode: AssistantMode;
  /** True iff the user explicitly opted into local key storage. */
  keysConsented: boolean;
  /** Show folded opponents' hole cards in review (spec §4.8 L311). */
  showFoldedCards: boolean;
  /** Schema version for the persisted slice — bumped on breaking changes. */
  schemaVersion: number;

  setProvider(provider: LlmProvider): void;
  saveKey(provider: LlmProvider, key: string): void;
  clearKey(provider: LlmProvider): void;
  setAssistantMode(mode: AssistantMode): void;
  setKeysConsented(consented: boolean): void;
  setShowFoldedCards(show: boolean): void;
  reset(): void;
};

export const SETTINGS_SCHEMA_VERSION = 1;
export const SETTINGS_STORAGE_KEY = 'poker-settings-v1';

const DEFAULT_KEYS: Record<LlmProvider, string> = {
  openai: '',
  anthropic: '',
  google: '',
};

const initialState: Pick<
  SettingsState,
  | 'llmProvider'
  | 'apiKeys'
  | 'assistantMode'
  | 'keysConsented'
  | 'showFoldedCards'
  | 'schemaVersion'
> = {
  llmProvider: 'openai',
  apiKeys: DEFAULT_KEYS,
  assistantMode: 'both',
  keysConsented: false,
  showFoldedCards: true,
  schemaVersion: SETTINGS_SCHEMA_VERSION,
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      ...initialState,

      setProvider(provider) {
        set({ llmProvider: provider });
      },

      saveKey(provider, key) {
        set((s) => ({ apiKeys: { ...s.apiKeys, [provider]: key } }));
      },

      clearKey(provider) {
        set((s) => ({ apiKeys: { ...s.apiKeys, [provider]: '' } }));
      },

      setAssistantMode(mode) {
        set({ assistantMode: mode });
      },

      setKeysConsented(consented) {
        set({ keysConsented: consented });
      },

      setShowFoldedCards(show) {
        set({ showFoldedCards: show });
      },

      reset() {
        set(initialState);
      },
    }),
    {
      name: SETTINGS_STORAGE_KEY,
      version: SETTINGS_SCHEMA_VERSION,
      // Persist only what survives a refresh: provider preference, assistant
      // mode, consent flag, and (when consented) API keys. Without consent
      // the apiKeys map is replaced with empty strings so a stale localStorage
      // entry never leaks a key the user has revoked consent for.
      partialize: (state) => ({
        llmProvider: state.llmProvider,
        assistantMode: state.assistantMode,
        keysConsented: state.keysConsented,
        showFoldedCards: state.showFoldedCards,
        schemaVersion: state.schemaVersion,
        apiKeys: state.keysConsented ? state.apiKeys : DEFAULT_KEYS,
      }),
    },
  ),
);
